import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { KickstarterFormData } from '../types';

const formSchema = z.object({
  email: z
    .string()
    .email('Please enter a valid email address')
    .min(1, 'Email is required')
});

export const useKickstarterForm = () => {
  const form = useForm<KickstarterFormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: ''
    }
  });

  const onSubmit = async (data: KickstarterFormData) => {
    try {
      // Log form submission
      console.log('📧 Form submission:', data);
      
      // Get environment information
      const isDev = import.meta.env.DEV;
      
      // DEV MODE: Use mock API or actual network call based on URL parameter
      if (isDev) {
        console.log('🧪 Development mode detected');
        
        // Allow testing with real API call via URL parameter ?api=true
        const useRealApi = window.location.search.includes('api=true');
        
        if (!useRealApi) {
          console.log('🔸 Using mock API response (add ?api=true to URL to test real API)');
          
          // Simulate network delay
          await new Promise(resolve => setTimeout(resolve, 1000));
          
          return { 
            success: true, 
            message: `[DEV MODE] Email ${data.email} would be sent to Klaviyo in production` 
          };
        }
        
        console.log('🔸 Testing with real API call (remove ?api=true from URL to use mock)');
      }
      
      // PRODUCTION MODE or DEV testing mode: Make actual API calls
      
      // First try the Edge API route
      let response = null;
      let result = null;
      
      console.log('🔄 Attempting to contact API endpoint...');
      
      // Force application/json content type
      const fetchOptions = {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Cache-Control': 'no-cache'
        },
        body: JSON.stringify(data)
      };
      
      try {
        console.log('🔄 Trying Edge API route...');
        
        // Use absolute URL to avoid proxying issues
        const baseUrl = window.location.origin;
        const apiUrl = `${baseUrl}/api/subscribe`;
        
        console.log(`🔄 Fetching ${apiUrl}`);
        response = await fetch(apiUrl, fetchOptions);
        
        // Log the response status
        console.log(`📩 API Response Status (Edge): ${response.status}`);
        
        result = await response.json();
        
        // Log the response data
        console.log('📩 API Response (Edge):', result);
      } catch (edgeError) {
        // If Edge API fails, try Node.js API route as fallback
        console.log('⚠️ Edge API failed, trying Node.js API route instead:', edgeError);
        
        try {
          const baseUrl = window.location.origin;
          const fallbackUrl = `${baseUrl}/api/subscribe-node`;
          
          console.log(`🔄 Fetching fallback ${fallbackUrl}`);
          response = await fetch(fallbackUrl, fetchOptions);
          
          // Log the response status
          console.log(`📩 API Response Status (Node): ${response.status}`);
          
          result = await response.json();
          
          // Log the response data
          console.log('📩 API Response (Node):', result);
        } catch (nodeError) {
          console.error('⚠️ Both API routes failed:', nodeError);
          
          // In development, return a mock success response
          if (isDev) {
            return { 
              success: true, 
              message: `[DEV MODE] Email ${data.email} would be sent to Klaviyo in production (API calls failed)` 
            };
          }
          
          throw new Error('Failed to contact API server. Please try again later.');
        }
      }

      // We got a response, but check if it was successful
      if (!response || !response.ok) {
        const errorMessage = result?.error || 'Failed to subscribe';
        console.error('⚠️ API returned error:', errorMessage);
        throw new Error(errorMessage);
      }
      
      // Development mode message override for successful API calls
      const message = isDev 
        ? `[DEV MODE] Successfully contacted API with email: ${data.email}` 
        : result.message || 'Successfully subscribed to Kickstarter updates!';
      
      return { 
        success: true,
        message
      };
    } catch (error) {
      console.error('⚠️ Error submitting form:', error);
      return { 
        success: false, 
        error: error instanceof Error 
          ? error.message 
          : 'Failed to submit form. Please try again.' 
      };
    }
  };

  return {
    form,
    onSubmit
  };
};